import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { db } from '@db/database';

export type DownloadStatus =
  | 'not_cached'
  | 'downloading'
  | 'cached'
  | 'update_available'
  | 'error';

type DownloadProgress = {
  jobNumber: string;
  progress: number;
  totalSize?: number;
  downloadedSize?: number;
};

type DownloadQueueItem = {
  jobNumber: string;
  priority: number;
  addedAt: Date;
};

type StorageStats = {
  totalSize: number;
  usedSize: number;
  availableSize?: number;
  itemCount: number;
};

type DownloadStore = {
  downloadStatuses: Map<string, DownloadStatus>;
  downloadProgress: Map<string, DownloadProgress>;
  downloadQueue: DownloadQueueItem[];
  storageStats: StorageStats;
  isProcessingQueue: boolean;

  setDownloadStatus: (jobNumber: string, status: DownloadStatus) => void;
  setDownloadProgress: (
    jobNumber: string,
    progress: number,
    totalSize?: number,
    downloadedSize?: number
  ) => void;
  addToQueue: (jobNumber: string, priority?: number) => void;
  removeFromQueue: (jobNumber: string) => void;
  clearQueue: () => void;
  setStorageStats: (stats: StorageStats) => void;
  updateStorageStats: () => Promise<void>;
  setIsProcessingQueue: (isProcessing: boolean) => void;

  initializeStatuses: () => Promise<void>;
  getDownloadStatus: (jobNumber: string) => DownloadStatus;
  getDownloadProgress: (jobNumber: string) => DownloadProgress | undefined;
  isDownloading: (jobNumber: string) => boolean;
  isCached: (jobNumber: string) => boolean;

  clearDownloadData: (jobNumber: string) => Promise<void>;
  clearAllDownloads: () => Promise<void>;
};

export const useDownloadStore = create<DownloadStore>()(
  devtools(
    (set, get) => ({
      downloadStatuses: new Map(),
      downloadProgress: new Map(),
      downloadQueue: [],
      storageStats: {
        totalSize: 0,
        usedSize: 0,
        itemCount: 0
      },
      isProcessingQueue: false,

      setDownloadStatus: (jobNumber, status) => {
        set(state => {
          const newStatuses = new Map(state.downloadStatuses);
          newStatuses.set(jobNumber, status);

          if (status !== 'downloading') {
            const newProgress = new Map(state.downloadProgress);
            newProgress.delete(jobNumber);
            return {
              downloadStatuses: newStatuses,
              downloadProgress: newProgress
            };
          }

          return { downloadStatuses: newStatuses };
        });
      },

      setDownloadProgress: (jobNumber, progress, totalSize, downloadedSize) => {
        set(state => {
          const newProgress = new Map(state.downloadProgress);
          newProgress.set(jobNumber, {
            jobNumber,
            progress,
            totalSize,
            downloadedSize
          });
          return { downloadProgress: newProgress };
        });
      },

      addToQueue: (jobNumber, priority = 0) => {
        set(state => {
          const existingIndex = state.downloadQueue.findIndex(
            item => item.jobNumber === jobNumber
          );
          if (existingIndex >= 0) {
            return state;
          }

          const newQueue = [
            ...state.downloadQueue,
            {
              jobNumber,
              priority,
              addedAt: new Date()
            }
          ];

          newQueue.sort((a, b) => {
            if (a.priority !== b.priority) {
              return b.priority - a.priority;
            }
            return a.addedAt.getTime() - b.addedAt.getTime();
          });

          return { downloadQueue: newQueue };
        });
      },

      removeFromQueue: jobNumber => {
        set(state => ({
          downloadQueue: state.downloadQueue.filter(
            item => item.jobNumber !== jobNumber
          )
        }));
      },

      clearQueue: () => {
        set({ downloadQueue: [] });
      },

      setStorageStats: stats => {
        set({ storageStats: stats });
      },

      updateStorageStats: async () => {
        try {
          const metadata = await db.downloadMetadata.toArray();
          const totalSize = metadata.reduce(
            (sum, item) => sum + (item.size || 0),
            0
          );
          const itemCount = metadata.filter(
            item => item.status === 'cached'
          ).length;

          let availableSize: number | undefined;
          if ('storage' in navigator && 'estimate' in navigator.storage) {
            const estimate = await navigator.storage.estimate();
            availableSize = estimate.quota
              ? estimate.quota - (estimate.usage || 0)
              : undefined;
          }

          set({
            storageStats: {
              totalSize,
              usedSize: totalSize,
              availableSize,
              itemCount
            }
          });
        } catch {
          // Silently handle quota calculation errors
        }
      },

      setIsProcessingQueue: isProcessing => {
        set({ isProcessingQueue: isProcessing });
      },

      initializeStatuses: async () => {
        try {
          const metadata = await db.downloadMetadata.toArray();
          const newStatuses = new Map<string, DownloadStatus>();

          metadata.forEach(item => {
            if (item.status === 'downloading') {
              newStatuses.set(item.jobNumber, 'error');
            } else {
              newStatuses.set(item.jobNumber, item.status);
            }
          });

          set({ downloadStatuses: newStatuses });
          await get().updateStorageStats();
        } catch {
          // Silently handle initialization errors
        }
      },

      getDownloadStatus: jobNumber => {
        return get().downloadStatuses.get(jobNumber) || 'not_cached';
      },

      getDownloadProgress: jobNumber => {
        return get().downloadProgress.get(jobNumber);
      },

      isDownloading: jobNumber => {
        return get().downloadStatuses.get(jobNumber) === 'downloading';
      },

      isCached: jobNumber => {
        const status = get().downloadStatuses.get(jobNumber);
        return status === 'cached' || status === 'update_available';
      },

      clearDownloadData: async jobNumber => {
        await db.transaction(
          'rw',
          db.cachedWorkOrders,
          db.cachedWorkOrderDetails,
          db.downloadMetadata,
          async () => {
            await db.cachedWorkOrders
              .where('jobNumber')
              .equals(jobNumber)
              .delete();
            await db.cachedWorkOrderDetails
              .where('jobNumber')
              .equals(jobNumber)
              .delete();
            await db.downloadMetadata
              .where('jobNumber')
              .equals(jobNumber)
              .delete();
          }
        );

        set(state => {
          const newStatuses = new Map(state.downloadStatuses);
          newStatuses.delete(jobNumber);
          return { downloadStatuses: newStatuses };
        });

        await get().updateStorageStats();
      },

      clearAllDownloads: async () => {
        await db.transaction(
          'rw',
          db.cachedWorkOrders,
          db.cachedWorkOrderDetails,
          db.downloadMetadata,
          async () => {
            await db.cachedWorkOrders.clear();
            await db.cachedWorkOrderDetails.clear();
            await db.downloadMetadata.clear();
          }
        );

        set({
          downloadStatuses: new Map(),
          downloadProgress: new Map(),
          storageStats: {
            totalSize: 0,
            usedSize: 0,
            itemCount: 0
          }
        });
      }
    }),
    {
      name: 'download-store'
    }
  )
);
